import { useMemo, useRef, useState, useEffect } from "react";
import { Box, FormControl, Slide, Snackbar } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import useLogout from "../../../hooks/useLogout";
import { useLingui } from "@lingui/react";
import { Controller, useForm } from "react-hook-form";
import { Alert, Divider, Grid, InputLabel, MenuItem, Paper, Select, Typography, useTheme } from "@mui/material/";
import useHttp from "../../../hooks/useHttp";
import httpReq from "../../../ce-services/http-req";
import { msg, Trans } from '@lingui/macro';
import UserControls from "../../desktop/UserControls";
import StatisticsFilterPanel from "../StatisticsFilterPanel";
import EChart from "./EChart";
import {
  getDashboardColorMapForChartType,
  getRandomColors,
} from "./dashboard-random-palette";
import {
  statisticsMapGradientColors,
} from "./statistics-chart-palette";
import { getChartColumnGroupingModel, isSensitiveStatsField } from "../../../pages/Statistics/utils";
import {
  getDefaultStatsReportType,
  isNonInputAnalystUser,
} from "../../../pages/Statistics/statistics-report-type";
import { enrichStatsRequestPayload } from "../../../pages/Statistics/statistics-dataset-context";
import { formatStatsApiError } from "../stats-api-errors";
import { StatsDatasetControls } from "../StatsDatasetControls";
import useHttpApiNew from "../../../hooks/useHttpApiNew";
import { NewApiBaseUrl } from "../../../consts/new-api-base-url";
import { NAV_LAYOUT_MARGIN_START } from "../../../consts/nav-layout-margin";
import {
  buildStatsRequestPayload,
  normalizeStatsRowsToChartData,
  unwrapStatsChartRows,
} from "./stats-response-utils";
import { useConfigLocale } from "../../../hooks/use-config-locale";

const getStatsUrl = new URL(`${NewApiBaseUrl}/Stats`);

/**
 * @param {string} chartType
 * @param {string[]} ordinalColors
 */
function colorMapForCustomChartType(chartType, ordinalColors) {
  return getDashboardColorMapForChartType(
    chartType,
    statisticsMapGradientColors,
    ordinalColors,
  );
}

const baseSelectFieldSx = (theme, isDark) => ({
  backgroundColor: isDark ? theme.palette.newColors.lightGray : "#f5f5f5",
  color: isDark ? theme.palette.newColors.gray : "#3C3C3B",
  borderRadius: 1,
  "& label": {
    color: theme.palette.newColors.gray
  },

  "& .MuiSelect-select": {
    color: isDark ? theme.palette.newColors.gray : "#3C3C3B"
  },
  '& .MuiOutlinedInput-root': {
    color: theme.palette.newColors.gray
  },
  "& .MuiSelect-icon": {
    color: theme.palette.newColors.gray
  },
  "& fieldset": { border: "none" },
  "&:hover fieldset": { border: "none" },
  "&.Mui-focused fieldset": { border: "none" }
});

const CustomChart = ({
  isMobile
}) => {

  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const { _ } = useLingui();
  const configLocale = useConfigLocale();
  const state = location.state;
  const token = state?.token || null;
  const init = useMemo(() => state?.init || {}, [state?.init]);
  const fieldsMethods = useForm({
    defaultValues: {
      reportType: getDefaultStatsReportType(init?.user),
      target: '',
      fieldName: '',
      groupingModel: [],
    },
  });
  const [user, setUser] = useState({});
  const theme = useTheme();
  const { sendRequest, error } = useHttp();
  const [formModel, setFormModel] = useState({});
  const [formList, setFormList] = useState([]);
  const [columnGroupingModel, setColumnGroupingModel] = useState([]);
  const [chartTargetItems, setChartTargetItems] = useState([]);
  const [chartFieldItems, setChartFieldItems] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [canAnalize, setCanAnalize] = useState(null);
  const [message, setMessage] = useState('');
  const submitRef = useRef();
  const { request } = useHttpApiNew();

  const isDark = theme.palette.mode === "dark";

  useEffect(() => {
    if (!token) return logout();
    if (isMobile) return navigate('/home', { state: { token, init } });
  }, [token, logout, isMobile, navigate, init]);

  useEffect(() => {
    const { configuration, user } = init;
    setUser(user);
    setFormModel(configuration);
    const initForms = Object.keys(configuration).sort((a, b) => configuration[a].position - configuration[b].position);
    setFormList(initForms);
    const columnGroupingModel = getChartColumnGroupingModel(configuration);
    setColumnGroupingModel(columnGroupingModel);
    const defaultReportType = getDefaultStatsReportType(init.user);
    fieldsMethods.setValue('reportType', defaultReportType);
  }, [init, _, configLocale]);

  useEffect(() => {
    if (!columnGroupingModel) return;
    if (columnGroupingModel.length === 0) return;
    const arrChartFieldItems = columnGroupingModel.map((c) => {
      return c.groupId;
    });
    setChartTargetItems(arrChartFieldItems);
    fieldsMethods.setValue('groupingModel', columnGroupingModel);
    fieldsMethods.setValue('formModel', formModel);
  }, [columnGroupingModel]);

  const handleFilterSubmit = async (searchParams) => {


    const statSearchParams =
    Object.entries(searchParams).map((e) => {
      return {
        name: e[0],
        value: e[1]
      };
    });
    const fieldData = fieldsMethods.getValues();
    if (
      isSensitiveStatsField(
        init.configuration,
        fieldData.target,
        fieldData.fieldName,
      )
    ) {
      setMessage(_(msg`This field is marked as sensitive and cannot be used in charts.`));
      setChartData(null);
      return;
    }

    const payloadData = { ...fieldData };
    delete payloadData.groupingModel;
    delete payloadData.formModel;
    const isCrashDate = fieldData.fieldName === 'Crash date';
    const isPeopleDistinctField =
    fieldData.target === 'people' &&
    ['Alcohol use', 'Safety equipment use'].includes(fieldData.fieldName);

    const payload = enrichStatsRequestPayload(
      buildStatsRequestPayload({
        filters: statSearchParams,
        chartField: payloadData,
        fields: init.configuration,
        groupBy: isCrashDate ? 'yyyy' : undefined,
        countMode: isPeopleDistinctField ? 'distinctCrashes' : undefined
      }),
      {
        user: init.user,
        organizations: init.organizations ?? [],
        reportType: fieldData.reportType,
      },
    );
    const res = await request(getStatsUrl, 'POST', payload, { Authorization: `Bearer ${token}` });
    const fieldCompleteName = fieldData.target + ' - ' + fieldData.fieldName;
    const chartStub = {
      type: 'bar',
      title: { text: fieldCompleteName },
      payload: {
        chartField: payload.chartField,
        groupBy: isCrashDate ? 'yyyy' : undefined,
        countMode: isPeopleDistinctField ? 'distinctCrashes' : undefined
      },
      groupByFormat: isCrashDate ? 'yyyy' : undefined
    };
    if (res.responseAnyError) {
      setMessage(formatStatsApiError(res, _));
      setChartData(null);
      return;
    }
    const chartRows = unwrapStatsChartRows(res.responseData);
    const result = normalizeStatsRowsToChartData({
      rawRows: chartRows,
      formModel: fieldData.formModel ?? init.configuration,
      chart: chartStub,
      translate: _
    });

    const ordinalColors = getRandomColors(Math.max(result.length, 1));

    const chart = {
      data: result,
      title: _(msg`Custom chart for`) + ' ' + fieldCompleteName,
      type: 'bar' // [bar, line, pie]
      // , xLabel: fieldCompleteName
      // , xLabelStyle: {transform: 'translateY(200px)'}
      , yLabel: _(msg`Count`),
      tickPlacement: 'middle',
      tickLabelPlacement: 'middle',
      tickLabelStyle: {
        angle: 45,
        textAnchor: 'start',
        fontSize: 12
      },
      margin: { bottom: 350, right: 150 },
      ordinalColors,
      colorMap: colorMapForCustomChartType('bar', ordinalColors)
      // , tickLabelStyle: {
      //     angle: 45
      //     , textAnchor: 'start'
      //     , fontSize: 12
      // }
      // , margin: {bottom:250, right: 150}
    };
    setChartData(chart);

  };

  const handleFilterReset = () => {
    fieldsMethods.setValue('target', null);
    fieldsMethods.setValue('fieldName', null);
    setChartData(null);
  };

  const onFieldFormSubmit = (data) => {

  };

  useEffect(() => {
    const { unsubscribe } = fieldsMethods.watch((value, { type }) => {
      if (type === 'change') {
        const currentValues = fieldsMethods.getValues();
        const selectedTarget = currentValues.target;
        const selectedField = currentValues.fieldName;

        if (!selectedTarget || !currentValues.groupingModel || currentValues.groupingModel.length === 0) {
          setChartFieldItems([]);
        } else {

          const found = currentValues.groupingModel.find((c) => {
            return c.groupId == selectedTarget;
          });
          const v = found.children;
          const values = Object.values(v).map((v) => {
            return v.field;
          }).filter((v) => {
            return v !== 'organization';
          });
          setChartFieldItems(values);
        }
        selectedTarget && selectedField ? setCanAnalize(true) : setCanAnalize(false);
      }
    });
    return () => unsubscribe();
  }, [fieldsMethods.getValues, fieldsMethods.watch]);

  const handleChartTypeChange = (event) => {
    const nextType = event.target.value;
    setChartData((old) => {
      if (!old) return old;
      const ordinalColors =
        old.ordinalColors ?? getRandomColors(Math.max(old.data?.length ?? 1, 1));
      return {
        ...old,
        type: nextType,
        ordinalColors,
        colorMap: colorMapForCustomChartType(nextType, ordinalColors),
      };
    });
  };

  function SlideTransition(props) {
    return <Slide {...props} direction="up" />;
  }

  return (
    <Box
      mb={4}
      pl={2}
      pr={2}
      display='flex'
      flexDirection='column'
      alignItems='center'
      sx={{
        marginInlineStart: NAV_LAYOUT_MARGIN_START,
        '& .overload-header': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText
        }
      }}>
      
            {/*
        <Box sx={{
           display: 'flex',
           flexDirection: 'row',
           justifyContent: 'space-between',
           alignItems: 'center',
           width: '100%',
           padding: 1.5
        }}>
           <Typography sx={{fontSize: 18}} variant="h3">
               <Trans>Custom chart</Trans>
           </Typography>
           <Box sx={{alignSelf: 'end'}}>
               <UserControls noElev={true} state={state}/>
           </Box>
        </Box>
        <Divider flexItem/>
        */}
            <Grid container spacing={2} sx={{ marginTop: 0 }}>
                <Grid item xs={12} lg={3}>
                    {chartTargetItems.length > 0 &&
          <Paper square sx={{
            display: 'flex',
            flexDirection: 'column',
            mt: 1,
            gap: 2,
            pt: 3,
            pb: 3,
            pl: 2,
            pr: 2,
            minHeight: 350
          }}>
                            <Box display='flex' justifyContent='space-between' alignItems='baseline'>
                                <Typography
                sx={{
                  color: theme.palette.mode === "light" ? theme.palette.newColors.gray : theme.palette.newColors.lightGray,
                  fontWeight: 600
                }}
                variant="h5">
                
                                    <Trans>Field to analyze</Trans>
                                </Typography>
                            </Box>
                            <Divider flexItem sx={{ mt: -1 }} />
                            <Box
              component="form"
              ref={submitRef}
              onSubmit={fieldsMethods.handleSubmit(onFieldFormSubmit)}
              display='flex'
              gap={2}
              pr={2}
              noValidate>
              
                                <Grid container spacing={0}>
                                    <Grid item xs={12}>
                                        <Box width='100%'>
                                        <StatsDatasetControls
                                          user={init.user}
                                          organizations={init.organizations ?? []}
                                          reportType={fieldsMethods.watch('reportType')}
                                          onReportTypeChange={(v) => fieldsMethods.setValue('reportType', v)}
                                          showBasicOrganization={false}
                                          variant="form"
                                          lockReportType={isNonInputAnalystUser(init?.user)}
                                          selectSx={baseSelectFieldSx}
                                          isDark={isDark}
                                          theme={theme}
                                        />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box width='100%'>
                                            <FormControl key={'cc-target-form-control'} margin='dense' fullWidth>
                                                <InputLabel id={'cc-target-input-label'}>{_(msg`Form`)}</InputLabel>
                                                <Controller
                        name={'target'}
                        control={fieldsMethods.control}
                        render={({ field }) =>
                        <Select
                          sx={{
                            ...baseSelectFieldSx(theme, isDark),
                            "&:hover": { backgroundColor: "inherit" } // no hover white
                          }}
                          {...field}
                          labelId='target-label'
                          id='target-label'
                          label={_(msg`Form`)}
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          fullWidth>
                          
                                                            {chartTargetItems.map((opt) => {
                            return <MenuItem key={`cc-target-${opt}`}
                            value={opt}>{opt}</MenuItem>;
                          })}
                                                        </Select>
                        } />
                      
                                            </FormControl>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box width='100%'>
                                            <FormControl key={'cc-field-form-control'} margin='dense' fullWidth>
                                                <InputLabel id={'cc-field-input-label'}>{_(msg`Field`)}</InputLabel>
                                                <Controller
                        name={'fieldName'}
                        control={fieldsMethods.control}
                        render={({ field }) =>
                        <Select
                          sx={{
                            ...baseSelectFieldSx(theme, isDark),
                            "&:hover": { backgroundColor: "inherit" } // no hover white
                          }}
                          {...field}
                          labelId='field-label'
                          id='field-label'
                          label={_(msg`Field`)}
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          fullWidth>
                          
                                                            {chartFieldItems.map((opt) => {
                            return <MenuItem key={`cc-field-${opt}`}
                            value={opt}>{opt}</MenuItem>;
                          })}
                                                        </Select>
                        } />
                      
                                            </FormControl>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
          }
                </Grid>
                <Grid item xs={12} lg={9}>
                    {formList.length > 0 &&
          <StatisticsFilterPanel
            newLabel={_(msg`Custom charts`)}
            title={_(msg`Custom charts`)}
            formList={formList}
            filterDisabled={!canAnalize}
            onFilterSubmit={handleFilterSubmit}
            onFilterReset={handleFilterReset}
            setDataLoaded={null}
            setFilters={null}
            user={user}
            formModel={formModel}
            isMobile={isMobile} />

          }

                </Grid>
                <Grid item xs={12} lg={12}>
                    <Box p={2} key={'custom-chart-box'} width='100%' display='flex'>
                        {/* First column */}
                        {chartData &&
            <EChart chart={chartData} index={0} handleCharts={handleChartTypeChange} width='100%' />}
                    </Box>
                </Grid>
            </Grid>
            <Snackbar
        open={message !== ''}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        TransitionComponent={SlideTransition}
        onClose={(_, reason) => {
          if (reason === 'clickaway') {
            return;
          }
          setMessage('');
        }}>
                <Alert variant='filled' severity='error' sx={{ width: '100%' }}>{message}</Alert>
            </Snackbar>
        </Box>);

};

export default CustomChart;